import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import useUnleashFeatureFlagAdapter from '../../adapter/UnleashFeatureFlagAdapter';
import {
  FEATURE_FLAGS,
  FEATURE_FLAG_TYPES,
  type FeatureFlagType,
} from '../../constants/FeatureFlag';
import type { RootState } from '../../../redux/types';

export type UseFeatureFlagOptions = {
  /** Bypass session cache + in-flight dedupe (always hits the network). */
  skipCache?: boolean;
  /**
   * Quando true, falha de rede NÃO vira "flag desligada" silenciosamente:
   * `flagErrored` fica true. Só vale para flags não-PERMISSION.
   */
  reportErrors?: boolean;
};

export type UseFeatureFlagResult = {
  isFeatureFlagEnable: boolean;
  flagsReady: boolean;
  flagResolved: boolean;
  /** Sempre presente no retorno do hook; opcional no tipo para não quebrar mocks de quem não usa. */
  flagErrored?: boolean;
};

const flattenFlags = (
  flagsGroup: typeof FEATURE_FLAGS,
): readonly string[] =>
  Object.values(flagsGroup).flatMap(group => Object.values(group));

const ALL_FLAGS = flattenFlags(FEATURE_FLAGS);

// Session cache (same idea as useFeatureFlagWithContext). Flags rarely flip
// mid-session; remounts of gates/pages were causing N identical GETs.
const flagCache = new Map<string, boolean>();
const flagInflight = new Map<string, Promise<boolean>>();

export const clearFeatureFlagCache = (): void => {
  flagCache.clear();
  flagInflight.clear();
};

const resolveFlagType = (flagName: string): FeatureFlagType | null => {
  if (!ALL_FLAGS.includes(flagName)) {
    // Sentry em vez de console: é erro de programação e precisa aparecer em produção.
    Sentry.captureMessage(
      `[useFeatureFlag] Feature flag "${flagName}" does not exist`,
      { level: 'warning' as Sentry.Severity, tags: { flag: flagName } },
    );
    return null;
  }

  if (
    (Object.values(FEATURE_FLAGS.PERMISSION) as string[]).includes(flagName)
  ) {
    return FEATURE_FLAG_TYPES.PERMISSION;
  }

  if ((Object.values(FEATURE_FLAGS.RELEASE) as string[]).includes(flagName)) {
    return FEATURE_FLAG_TYPES.RELEASE;
  }

  return FEATURE_FLAG_TYPES.KILL_SWITCH;
};

/**
 * Override local de flag, SOMENTE em desenvolvimento (NODE_ENV === 'development')
 * ou em build de E2E (REACT_APP_E2E === '1', valor de BAKE-TIME: entra no bundle
 * apenas quando o build é feito com essa variável — nunca no build de produção).
 * Chave: `ff:override:<NOME_DA_FLAG>` com valor 'true' | 'false'.
 * Sem chave → undefined (segue o fluxo normal). Em production/test sem
 * REACT_APP_E2E=1 a chave é ignorada e o webpack elimina este bloco (F4) —
 * desde que a variável esteja DEFINIDA no build (o script `build` da raiz passa
 * `REACT_APP_E2E=0`): o CRA injeta `process.env` como literal de objeto e o
 * Terser só dobra o acesso quando a chave existe nele. Sem a var, o bloco
 * sobrevive como código morto e a string `ff:override` vaza para o bundle.
 * Uso: .context/docs/05-devops-operations.md ("Override local de flag" e
 * "E2E contra build estático").
 */
export const FLAG_OVERRIDE_PREFIX = 'ff:override:';

const readDevOverride = (flagName: string): boolean | undefined => {
  const permitido =
    process.env.NODE_ENV === 'development' || process.env.REACT_APP_E2E === '1';
  if (!permitido) return undefined;
  try {
    const raw = window.localStorage.getItem(
      `${FLAG_OVERRIDE_PREFIX}${flagName}`,
    );
    if (raw === null) return undefined;
    return raw === 'true';
  } catch {
    return undefined;
  }
};

const buildCacheKey = (
  flagName: string,
  type: FeatureFlagType | null,
  userId?: string | number,
): string => {
  if (type === FEATURE_FLAG_TYPES.PERMISSION) {
    return `${flagName}::user:${userId ?? ''}`;
  }
  return flagName;
};

/**
 * @param flagName
 * @param options.skipCache bypass session cache + in-flight dedupe.
 *   Default keeps cache — same contract as before, fewer duplicate requests.
 *   KILL_SWITCH flags always skip cache regardless of this option.
 */
const useFeatureFlag = (
  flagName?: string,
  { skipCache = false, reportErrors = false }: UseFeatureFlagOptions = {},
): UseFeatureFlagResult => {
  const { isEnabled, isUserEnabled, flagsReady } =
    useUnleashFeatureFlagAdapter();
  const userId = useSelector((state: RootState) => state.auth?.user?.id);

  const [isFeatureFlagEnable, setIsFeatureFlagEnable] = useState(true);
  const [flagResolved, setFlagResolved] = useState(false);
  const [flagErrored, setFlagErrored] = useState(false);

  useEffect(() => {
    let mounted = true;
    // Um cleanup só para os três caminhos de saída do efeito.
    const desmontar = () => {
      mounted = false;
    };
    setFlagResolved(false);
    setFlagErrored(false);

    if (!flagName) {
      setIsFeatureFlagEnable(false);
      setFlagResolved(true);
      return desmontar;
    }

    // Adapter hoje sempre expõe flagsReady=true; quando false, não disparamos
    // fetch (evita cachear/inflight de promise pendente que nunca resolve).
    if (!flagsReady) return desmontar;

    const checkFlag = async () => {
      try {
        const type = resolveFlagType(flagName);
        if (type === null) {
          if (mounted) setIsFeatureFlagEnable(false);
          return;
        }

        // Override local de desenvolvimento — não passa por cache nem adapter.
        const override = readDevOverride(flagName);
        if (override !== undefined) {
          if (mounted) setIsFeatureFlagEnable(override);
          return;
        }

        const cacheKey = buildCacheKey(flagName, type, userId);
        // Kill switches must always hit the network — never cache/dedupe.
        const shouldSkipCache =
          skipCache || type === FEATURE_FLAG_TYPES.KILL_SWITCH;

        if (!shouldSkipCache && flagCache.has(cacheKey)) {
          if (mounted) setIsFeatureFlagEnable(flagCache.get(cacheKey)!);
          return;
        }

        if (!shouldSkipCache && flagInflight.has(cacheKey)) {
          const enabled = await flagInflight.get(cacheKey)!;
          if (mounted) setIsFeatureFlagEnable(enabled);
          return;
        }

        const resolveEnabled = (): Promise<boolean> =>
          type === FEATURE_FLAG_TYPES.PERMISSION
            ? isUserEnabled(flagName)
            : isEnabled(flagName, { rethrowOnError: reportErrors });

        const fetchPromise = resolveEnabled()
          .then(enabled => {
            if (!shouldSkipCache) {
              flagCache.set(cacheKey, enabled);
              flagInflight.delete(cacheKey);
            }
            return enabled;
          })
          .catch(error => {
            if (!shouldSkipCache) {
              flagInflight.delete(cacheKey);
            }
            throw error;
          });

        if (!shouldSkipCache) {
          flagInflight.set(cacheKey, fetchPromise);
        }

        const enabled = await fetchPromise;
        if (mounted) setIsFeatureFlagEnable(enabled);
      } catch (error) {
        // fail-closed (flag false) COM telemetria: antes o erro morria no console.
        Sentry.captureException(error, {
          tags: { feature: 'useFeatureFlag', flag: flagName },
        });
        console.error(error);
        if (mounted) {
          setIsFeatureFlagEnable(false);
          setFlagErrored(reportErrors);
        }
      } finally {
        if (mounted) setFlagResolved(true);
      }
    };

    void checkFlag();

    return desmontar;
  }, [flagName, flagsReady, isEnabled, isUserEnabled, userId, skipCache, reportErrors]);

  return {
    isFeatureFlagEnable,
    flagsReady,
    flagResolved,
    flagErrored,
  };
};

export default useFeatureFlag;
